
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Document } from './types';

export const useDocuments = () => {
  return useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      console.log('Fetching documents...');
      
      // 1. Fetch document metadata from documents table
      const { data: tableData, error: tableError } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (tableError) {
        console.error('Error fetching documents from table:', tableError);
        throw tableError;
      }
      
      // 2. Fetch files from storage to verify they exist
      const { data: storageData, error: storageError } = await supabase
        .storage
        .from('documents')
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' }
        });
      
      if (storageError) {
        console.error('Error listing storage files:', storageError);
        // Don't throw - we'll just use table data if storage fails
      }
      
      // Filter documents to only include those with files in storage
      const availableFiles = storageData?.map(file => file.name.toUpperCase()) || [];
      
      // Map table data and include a flag for file availability
      const documents = tableData?.map(doc => {
        const formattedFilename = doc.filename
          .replace(/^i-/i, 'I-')
          .replace(/^n-/i, 'N-')
          .replace(/^g-/i, 'G-')
          .toUpperCase()
          .replace(/-[a-z]{2}\.pdf$/i, '.pdf');
        
        const fileExists = storageData ? availableFiles.includes(formattedFilename) : true;
        
        return {
          ...doc,
          fileAvailable: fileExists
        };
      }) || [];
      
      console.log('Documents fetched:', documents);
      return documents as Document[];
    },
    staleTime: 0,
    refetchOnMount: true,
  });
};

export const useUpdateDocumentDescription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, description }: { id: string, description: string }) => {
      const { data, error } = await supabase
        .from('documents')
        .update({ description })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Error updating document description:', error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch the documents query
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};
